import { MenuItemVariantDto } from '../../generated-api';

/**
 * Parsed barcode result
 */
export interface WeightBarcodeResult {
  type: 'weight';
  itemCode: string; // The PLU/item identifier from the barcode
  weightKg: number; // Weight always normalized to kg
  rawBarcode: string;
}

export interface PriceBarcodeResult {
  type: 'price';
  itemCode: string;
  priceMinorUnits: number;
  rawBarcode: string;
}

export interface StandardBarcodeResult {
  type: 'standard';
  code: string;
  rawBarcode: string;
}

export type BarcodeResult = WeightBarcodeResult | PriceBarcodeResult | StandardBarcodeResult;

/**
 * Parse a scanned barcode
 */
export function parseBarcode(barcode: string): BarcodeResult {
  const cleaned = barcode.trim();

  // Check for variable-measure EAN-13 (prefix 2)
  if (cleaned.length === 13 && cleaned.startsWith('2')) {
    const prefix = cleaned.substring(0, 2);

    // Weight-embedded prefixes (23-29)
    if (['23', '24', '25', '26', '27', '28', '29'].includes(prefix)) {
      // For weight barcodes: prefix (2) + itemCode (5) + value (5) + check (1) = 13 digits in total
      // Example: 2302725212835 -> prefix: 23, itemCode: 02725, value: 21283
      const itemCode = cleaned.substring(2, 7); // 5 digits
      const valuePart = cleaned.substring(7, 12); // 5 digits
      const numericValue = parseInt(valuePart, 10);

      return {
        type: 'weight',
        itemCode,
        weightKg: numericValue / 1000, // grams to kg
        rawBarcode: cleaned,
      };
    }

    // Price-embedded prefixes (20-22)
    if (['20', '21', '22'].includes(prefix)) {
      // For price barcodes: prefix (2) + itemCode (5) + value (5) + check (1) = 13
      const itemCode = cleaned.substring(2, 7); // 5 digits
      const valuePart = cleaned.substring(7, 12); // 5 digits
      const numericValue = parseInt(valuePart, 10);
      return {
        type: 'price',
        itemCode,
        priceMinorUnits: numericValue,
        rawBarcode: cleaned,
      };
    }
  }

  // Standard code (could be short PLU, GTIN-8, GTIN-13, etc.)
  return {
    type: 'standard',
    code: cleaned,
    rawBarcode: cleaned,
  };
}

/**
 * Normalize PLU by removing leading zeros
 * "00242" → "242", "05093" → "5093", "0" → "0"
 */
export function normalizePlu(code: string | undefined): string {
  if (!code) return '';
  return code.replace(/^0+/, '') || '0';
}

/**
 * Check if a menu item matches a code
 * Handles zero-padded PLUs: barcode "00242" matches item.plu "242" or "0242"
 * Matches against both plu and gtin fields
 * @param allowPartialMatch - If true, allows partial matching (for weight/price embedded barcodes)
 * Optimized for performance: reduces string operations and early exits
 */
function itemMatchesCode(item: MenuItemVariantDto, code: string, allowPartialMatch = false): boolean {
  // Early exit if no code provided
  if (!code) return false;

  // Get trimmed values once
  const gtin = item.gtin?.trim();
  const plu = item.plu?.trim();

  // Exact matches first (fastest path, no normalization needed)
  if (gtin === code || plu === code) {
    return true;
  }

  // If no gtin or plu, no match possible
  if (!gtin && !plu) {
    return false;
  }

  // Normalize code once
  const normalizedCode = normalizePlu(code);
  if (!normalizedCode) return false;

  // Normalize GTIN/PLU only if needed (lazy evaluation)
  let normalizedGtin: string | undefined;
  let normalizedPlu: string | undefined;

  if (gtin) {
    normalizedGtin = normalizePlu(gtin);
    // Check normalized exact match for GTIN
    if (normalizedGtin === normalizedCode) {
      return true;
    }
  }

  if (plu) {
    normalizedPlu = normalizePlu(plu);
    // Check normalized exact match for PLU
    if (normalizedPlu === normalizedCode) {
      return true;
    }
  }

  // Partial match: only for weight/price embedded barcodes
  // Early exit if partial matching not allowed or code too short
  if (!allowPartialMatch || normalizedCode.length < 4) {
    return false;
  }

  // Partial matching - check prefix/suffix matches
  // Only check if GTIN/PLU is long enough (at least 4 chars)
  if (normalizedGtin && normalizedGtin.length >= 4) {
    if (normalizedGtin.startsWith(normalizedCode) || normalizedGtin.endsWith(normalizedCode)) {
      return true;
    }
    if (normalizedCode.startsWith(normalizedGtin) || normalizedCode.endsWith(normalizedGtin)) {
      return true;
    }
  }

  if (normalizedPlu && normalizedPlu.length >= 4) {
    if (normalizedPlu.startsWith(normalizedCode) || normalizedPlu.endsWith(normalizedCode)) {
      return true;
    }
    if (normalizedCode.startsWith(normalizedPlu) || normalizedCode.endsWith(normalizedPlu)) {
      return true;
    }
  }

  return false;
}

/**
 * Convert weight in kg to the item's price unit
 */
function convertToItemUnit(weightKg: number, priceType: string | undefined): number {
  switch (priceType) {
    case 'PER_GR':
    case 'PER_GRAM':
      return weightKg * 1000; // kg -> grams
    case 'PER_HG':
      return weightKg * 10; // kg -> hectograms
    case 'PER_KG':
      return weightKg; // kg -> kg
    default:
      return weightKg;
  }
}

/**
 * Convert weight in kg to grams (for unitQuantity in backend)
 * Backend always expects unitQuantity in grams as an integer >= 1
 */
export function convertToGrams(weightKg: number): number {
  return Math.max(1, Math.round(weightKg * 1000)); // kg -> grams, ensure >= 1
}

/**
 * Convert grams back to the item's price unit (for display)
 * This reverses the conversion done for unitQuantity
 */
export function convertGramsToItemUnit(grams: number, priceType: string | undefined): number {
  const weightKg = grams / 1000; // grams -> kg
  switch (priceType) {
    case 'PER_GR':
    case 'PER_GRAM':
      return grams; // Already in grams
    case 'PER_HG':
      return weightKg * 10; // kg -> hectograms
    case 'PER_KG':
      return weightKg; // kg -> kg
    default:
      return weightKg;
  }
}

/**
 * Get unit label for display
 */
export function getUnitLabel(priceType: string | undefined): string {
  switch (priceType) {
    case 'PER_GR':
    case 'PER_GRAM':
      return 'g';
    case 'PER_HG':
      return 'hg';
    case 'PER_KG':
      return 'kg';
    default:
      return 'stk';
  }
}

/**
 * Result of matching a barcode to a menu item
 */
export interface MatchResult {
  item: MenuItemVariantDto;
  quantity: number; // In the item's unit (grams, hg, kg, or 1 for fixed)
  unitLabel: string; // "g", "hg", "kg", "stk"
  calculatedPrice: number; // quantity × item.price
  isWeightItem: boolean;
  weightKg?: number; // Original weight in kg (for weight-based items)
}

// Cache price type checks to avoid repeated array lookups
const WEIGHT_PRICE_TYPES = ['PER_GR', 'PER_GRAM', 'PER_HG', 'PER_KG'] as const;
const WEIGHT_PRICE_TYPES_SET = new Set(WEIGHT_PRICE_TYPES);

function isWeightPriceType(priceType: string | undefined): boolean {
  return priceType ? WEIGHT_PRICE_TYPES_SET.has(priceType as typeof WEIGHT_PRICE_TYPES[number]) : false;
}

/**
 * Find a matching menu item from a scanned barcode
 * Optimized for performance: early exits and cached checks
 */
export function findItemByBarcode(barcode: string, items: MenuItemVariantDto[]): MatchResult | null {
  // Early exit if no items
  if (!items || items.length === 0) {
    return null;
  }

  const parsed = parseBarcode(barcode);

  if (parsed.type === 'weight') {
    // If weight is 0 (or effectively 0), treat as standard barcode (piece price)
    // This handles cases like 2302644000001 where weight is 00000
    if (parsed.weightKg === 0 || parsed.weightKg < 0.001) {
      // Treat as standard barcode - use itemCode for exact match (no partial matching)
      const item = items.find((i) => itemMatchesCode(i, parsed.itemCode, false));
      if (!item) return null;

      const isWeight = isWeightPriceType(item.priceType);

      return {
        item,
        quantity: isWeight ? 0 : 1, // Weight items need manual entry
        unitLabel: getUnitLabel(item.priceType),
        calculatedPrice: isWeight ? 0 : item.price,
        isWeightItem: isWeight,
      };
    }

    // Normal weight barcode with actual weight - allow partial matching
    const item = items.find((i) => itemMatchesCode(i, parsed.itemCode, true));
    if (!item) return null;

    const quantity = convertToItemUnit(parsed.weightKg, item.priceType);

    return {
      item,
      quantity,
      unitLabel: getUnitLabel(item.priceType),
      calculatedPrice: quantity * item.price,
      isWeightItem: true,
      weightKg: parsed.weightKg,
    };
  }

  if (parsed.type === 'price') {
    // Allow partial matching for price embedded barcodes
    const item = items.find((i) => itemMatchesCode(i, parsed.itemCode, true));
    if (!item) return null;

    // Price is embedded - back-calculate quantity from price
    const embeddedPrice = parsed.priceMinorUnits / 100;
    const isWeight = isWeightPriceType(item.priceType);

    if (isWeight && item.price > 0) {
      // Calculate quantity based on embedded price and item price per unit
      const quantity = embeddedPrice / item.price;
      return {
        item,
        quantity,
        unitLabel: getUnitLabel(item.priceType),
        calculatedPrice: embeddedPrice,
        isWeightItem: true,
      };
    }

    // For non-weight items, use embedded price directly
    return {
      item,
      quantity: 1,
      unitLabel: getUnitLabel(item.priceType),
      calculatedPrice: embeddedPrice,
      isWeightItem: false,
    };
  }

  // Standard barcode - match against gtin or plu (exact match only, no partial matching)
  const item = items.find((i) => itemMatchesCode(i, parsed.code, false));
  if (!item) return null;

  const isWeight = isWeightPriceType(item.priceType);

  return {
    item,
    quantity: isWeight ? 0 : 1, // Weight items need manual entry
    unitLabel: getUnitLabel(item.priceType),
    calculatedPrice: isWeight ? 0 : item.price,
    isWeightItem: isWeight,
  };
}
